import { Injectable, signal } from '@angular/core';
import { GroupedSalesItem, TimeRange } from 'src/app/models/core/chart-interfaces';
import { EMPTY_GROUPED_SALES_ITEMS } from 'src/app/types/sale-graph-data';
import { GlobalReportApiService } from './global-report-api.service';
import { HeaderKpisService } from './header-kpis.service';
import { latestRequestTracker } from 'src/app/utils/reports/latest-request';
import { NGXLogger } from 'ngx-logger';

/**
 * Estado de la pestaña "Ventas" del reporte de jefe de ventas / administrador.
 *
 * A PROPÓSITO NO CACHEA: cada `load*` dispara su request siempre. Los endpoints
 * `/sales-manager/*` vienen acotados por el rol activo que manda
 * `GlobalReportApiService`, así que el mismo evento devuelve números distintos
 * según el rol con el que se lo mire. Un guard del estilo `if (loaded) return;`
 * serviría los datos del rol anterior al volver a entrar a la pantalla.
 *
 * Si alguna vez hace falta cachear acá, la clave NO puede ser el evento solo:
 * tiene que pasar por `ReportScopeService.keyFor(...)`, que incluye el rol
 * activo (ver `HeaderKpisService` o `SalesByLocationService` como referencia).
 */
@Injectable({ providedIn: 'root' })
export class GlobalReportService {
  // ─── Gráfico histograma de ventas agrupadas ───
  readonly groupedSalesDataLoading = signal(false);
  readonly groupedSalesData = signal<GroupedSalesItem[]>([]);
  readonly selectedSalesRange = signal<TimeRange>('daily');
  // Al cambiar de rango rápido, la respuesta del rango anterior se descarta.
  private readonly salesChartRequest = latestRequestTracker();

  constructor(
    private globalReportApi: GlobalReportApiService,
    private headerKpisService: HeaderKpisService,
    private logger: NGXLogger,
  ) { }

  // ─── Sales Type Graph ───

  /** Gráfico de tipo de venta del evento actual; sin rango, usa el elegido. */
  loadSalesTypeData(range?: TimeRange): void {
    const eventId = this.headerKpisService.eventId;
    if (!eventId) return;

    const selectedRange = range ?? this.selectedSalesRange();
    this.selectedSalesRange.set(selectedRange);
    this.groupedSalesDataLoading.set(true);
    const isLatest = this.salesChartRequest.start();

    this.globalReportApi
      .getSalesTypeGraph(eventId, selectedRange)
      .subscribe({
        next: (data) => {
          if (!isLatest()) return;
          this.groupedSalesData.set(data ?? EMPTY_GROUPED_SALES_ITEMS);
        },
        error: (error: unknown) => {
          if (!isLatest()) return;
          this.logger.error('Error cargando el gráfico de ventas (Jefe/Admin)', { eventId, range: selectedRange, error });
          this.groupedSalesData.set(EMPTY_GROUPED_SALES_ITEMS);
          this.groupedSalesDataLoading.set(false);
        },
        complete: () => {
          if (isLatest()) this.groupedSalesDataLoading.set(false);
        },
      });
  }

  // ─── Reset ───
  resetSelectedSalesRange(): void {
    this.selectedSalesRange.set('daily');
  }

  resetAllState(): void {
    this.groupedSalesData.set([]);
    this.groupedSalesDataLoading.set(false);
    this.selectedSalesRange.set('daily');
  }
}
